import { FileUploader } from "react-drag-drop-files"
import Spinner from "~/components/ui/Spinner"
import Alert from "~/components/ui/Alert"
import Accordian from "~/components/ui/Accordian"
import { useState } from "react"
import { useCamp } from "~/api2/hooks/useCamp"
import { useCamperImport, CamperImportResult } from "~/api2/hooks/useCamperImport"

type ImportResult = CamperImportResult

const CamperImport = () => {
  const {
    index: { data: camp },
  } = useCamp()
  const importMutation = useCamperImport()
  const [result, setResult] = useState<ImportResult | null>(null)

  const handleImport = async (file: File) => {
    if (!camp?.id) return

    setResult(null)
    try {
      const data = await importMutation.mutateAsync({ campId: camp.id, file })
      setResult(data)
    } catch {
      // error handled in hook
    }
  }

  return (
    <div className="space-y-6">
      <div className="my-6 space-y-2">
        <div className="text-xl font-semibold text-gray-900">Camper Import</div>
        <div className="font-medium text-gray-500">
          Upload a CSV file to import campers into your camp. The CSV should contain at least name, email, phone and
          address.
        </div>
      </div>
      {camp?.name && (
        <Alert
          kind="info"
          className="mb-4"
          description={
            <>
              You are importing campers into <strong>{camp.name}</strong>
            </>
          }
        />
      )}
      <div>
        <FileUploader handleChange={handleImport} name="csv" types={["CSV"]} multiple={false}>
          <div className="border-2 border-dashed border-gray-300 p-12 rounded-md text-center cursor-pointer hover:bg-gray-75">
            {importMutation.isPending ? (
              <div className="flex justify-center">
                <Spinner size={8} />
              </div>
            ) : (
              <span className="text-gray-600 text-md">Click or drag a CSV file here to import campers</span>
            )}
          </div>
        </FileUploader>
      </div>

      {result && (
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 rounded-md bg-gray-50 p-4 text-center">
              <div className="text-sm font-medium text-gray-500">Total rows processed</div>
              <div className="mt-1 text-xl font-semibold text-gray-900">
                {result.created.length + result.errors.length}
              </div>
            </div>
            <div className="flex-1 rounded-md bg-gray-50 p-4 text-center">
              <div className="text-sm font-medium text-gray-500">Rows succeeded</div>
              <div className="mt-1 text-xl font-semibold text-gray-900">{result.created.length}</div>
            </div>
            <div className="flex-1 rounded-md bg-gray-50 p-4 text-center">
              <div className="text-sm font-medium text-gray-500">Rows failed</div>
              <div className="mt-1 text-xl font-semibold text-gray-900">{result.errors.length}</div>
            </div>
          </div>

          <Accordian label={`Successful imports (${result.created.length})`}>
            <div className="mt-2 overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Row
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Name
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Email
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Phone
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {result.created.map(({ row, first_name, last_name, email, phone_country_code, phone_number }) => (
                    <tr key={row}>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{row}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                        {first_name} {last_name}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{email}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                        {phone_country_code}
                        {phone_number}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Accordian>

          <Accordian label={`Errors (${result.errors.length})`} defaultOpen>
            <div className="mt-2 overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Row
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Name
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Email
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Error Message
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {result.errors.map(({ row, message, first_name, last_name, email }) => (
                    <tr key={row}>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{row}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                        {first_name} {last_name}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{email}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Accordian>
        </div>
      )}
    </div>
  )
}

export default CamperImport
